<template>
  <div id="app">
    <keep-alive>
      <component :is="layout">
        <router-view />
      </component>
    </keep-alive>
  </div>
</template>

<script async>
import { storedCartToken } from '@vue-storefront/core/modules/cart/helpers/cartTokenCookie'
import config from 'config';
import { mapActions, mapState, mapGetters } from 'vuex'
import { isServer } from '@vue-storefront/core/helpers'
import { Logger } from '@vue-storefront/core/lib/logger'
import { getCookieValueCSR } from '@vue-storefront/core/scripts/utils/cookies';

const DefaultLayout = () => import(/* webpackChunkName: "vsf-layout-default" */ './layouts/Default');
const EmptyLayout = () => import(/* webpackChunkName: "vsf-layout-empty" */ './layouts/Empty');
const MinimalLayout = () => import(/* webpackChunkName: "vsf-layout-minimal" */ './layouts/Minimal');
const AnywhereLayout = () => import(/* webpackChunkName: "vsf-layout-anywhere" */ './layouts/Anywhere');
const ShopLayout = () => import(/* webpackChunkName: "vsf-layout-shop" */ './layouts/Shop');

export default {
  data () {
    return {
      ordersData: []
    }
  },
  computed: {
    ...mapGetters({
      currentPartner: 'partners/getCurrentPartner',
      isLoggedIn: 'user/isLoggedIn'
    }),
    ...mapState({
      overlayActive: state => state.ui.overlay
    }),
    layout () {
      return `${(this.$route.meta.layout || 'default')}-layout`
    }
  },
  methods: {
    ...mapActions({
      ensureCurrentPartner: 'partners/ensureCurrentPartner',
      changeCurrentPartnersCategory: 'partner_categories/setCurrentCategory',
      resetTags: 'partner_tags/resetSelectedTags'
    }),
    setPartnerFromRoute () {
      if (!this.$route.params.store) {
        return
      }
      this.ensureCurrentPartner({ code: this.$route.params.store, locationCode: this.$route.params.locationCode })
    },
    setPartnersCategoryFromRoute () {
      if (!this.$route.params.listId) {
        return
      }
      // Also loads the category's tags.
      this.changeCurrentPartnersCategory(this.$route.params.listId)
        .catch(err => Logger.error(err, 'sync')())
    },
    onServiceWorkerUpdated (registration) {
      if (!registration || !registration.waiting) return
      this.$store.dispatch('notification/spawnNotification', {
        type: 'info',
        message: this.$t('A new version of the app is available.'),
        hasNoTimeout: true,
        action1: {
          label: this.$t('Refresh'),
          action: () => registration.waiting.postMessage({ type: 'SKIP_WAITING' })
        },
        action2: { label: this.$t('Later'), action: 'close' }
      })
    }
  },
  created () {
    this.$nextTick(async () => {
      if (isServer) return;

      let language = getCookieValueCSR('shop/languages/language') || window.navigator.language;
      if (!config.i18n.availableLocale.includes(language)) {
        language = config.i18n.defaultLocale;
      }
      if (language) {
        await this.$store.dispatch('languages/changesCurrenLanguage', language);
      }
    })
  },
  async mounted () {
    if (!isServer) {
      this.$bus.$on('application-after-sw-updated', this.onServiceWorkerUpdated)
    }
    if (!isServer && !storedCartToken()) await this.$store.dispatch('cart/connect')
    if (!isServer && storedCartToken() && !this.$store.getters['cart/getCartToken']) this.$store.dispatch('cart/setCartToken', storedCartToken())

    Object.keys(localStorage)
      .forEach((key) => {
        let orderStr = key.search('shop/orders/');
        if (orderStr === 0) {
          localStorage.removeItem(key)
        }
      });
  },
  beforeDestroy () {
    if (!isServer) {
      this.$bus.$off('application-after-sw-updated', this.onServiceWorkerUpdated)
    }
  },
  watch: {
    '$route.params.store': function () {
      this.setPartnerFromRoute()
    },
    '$route.params.listId': function () {
      this.setPartnersCategoryFromRoute()
    },
    '$route': function () {
      let isSelectedTeg = this.$store.state.partner_tags.selected_tags && this.$store.state.partner_tags.selected_tags.length
      if ((!this.currentPartner && !this.$route.params.hasOwnProperty('listId')) && isSelectedTeg) {
        this.resetTags()
      }
    }
  },
  components: {
    DefaultLayout,
    EmptyLayout,
    MinimalLayout,
    AnywhereLayout,
    ShopLayout
  }
}
</script>
